import Visualization from "./visualization";
import {DataPointAggregationType, DataPointValueFormatType} from "~/enums/visualizationType";
import {NumberHelper} from "~/helpers/numberHelper";

// returns all the column headers across column data points array without total column
export const getColumnHeaders = (visualization: Visualization) => {
  return visualization.columnDataPointsArray.reduce((headers, dp) => {
    return headers.concat(dp.getDataHeaders())
  }, []);
}

// combines/returns live data column headers with total column
export const getAllHeaders = (visualization) => {
  const fullHeaderList = visualization.columnDataPointsArray.reduce((arr, point) => {
    arr = arr.concat(point.getDataHeaders().sort((a: any, b: any) => parseFloat(a) - parseFloat(b)).map(h => {
      return {
        title: h,
        client_id: point.client_id
      }
    }));

    if (point.enable_total) {
      arr.push({
        title: "Total",
        client_id: point.client_id,
      });
    }

    return arr;
  }, []);

  return fullHeaderList.filter(h => h.title === "Total" || getColumnHeaders(visualization).includes(h.title));
}

// Only used for the builder!
// returns an array of objects that contain the rowspan and columnspan so that the drag value container fills a section of the table in the builder
export const getTableSpanLengths = (visualization) => {
  const numOfSections = getTableHeaders(visualization).filter(h => h.isHeader).map(h => h.c_span);
  const lengths = new Array(numOfSections.length).fill({}).map(d => { return { c_span: 0, client_id: '' } });
  let currIndex = -1;

  getTableHeaders(visualization).forEach(h => {
    if (!h) {
      return;
    }

    if (h.isHeader) {
      currIndex += 1;
      lengths[currIndex].client_id = h.client_id;
    }

    lengths[currIndex].c_span += h.c_span;
  });

  return lengths;
}

// Gets the Top headers for data table including Total columns
export const getTableHeaders = (visualization) => {
  return visualization.tableHeader();
}

// Attempts to return the live data rows in combination with the headers/total rows
export const getRowHeaders = (visualization) => {
  if (!visualization.rowDataPointsArray.length) {
    return ["", ""];
  }

  return getRowLabels(visualization);
}

// maps over rowDataPoints array and puts all rows into a list including Total rows
export const getRowLabels = (visualization) => {
  return visualization.rowDataPointsArray.map(d => {
    let values = d.getDataHeaders().map(h => {
      return {
        title: h,
        isHeader: false,
        client_id: d.client_id,
      }
    }).sort((a: any, b: any) => parseFloat(a.title) - parseFloat(b.title));

    const header = {
      title: d.title,
      isHeader: true,
      r_span: d.getDataHeaders().length,
      client_id: d.client_id,
    };

    values.unshift(header);

    if (d.enable_total) {
      values.push({
        isHeader: false,
        title: "Total",
        client_id: d.client_id,
      })

      header.r_span++;
    }

    return values;
  }).flat();
}

export const getValueData = (visualization): string => {
  if ((!visualization.hasData || !visualization.valueDataPointsArray.length)) {
    return Math.round((Math.random() * 101)).toString();
  }
}

export const formatTable = (visualization, liveData, pageX, pageY) => {
  const PAGE_SIZE = 30;

  liveData.forEach(section => {
    // find the column data point for the section
    const colDataPoint = visualization.columnDataPointsArray.find(p => p.client_id === section["col_id"]);
    const rowDataPoint = visualization.rowDataPointsArray.find(p => p.client_id === section["row_id"]);
    const valueDataPoint = visualization.valueDataPointsArray.find(p => p.client_id === section["val_id"]);
    const rowBuckets = section["row_agg_c"]["buckets"].slice((pageY - 1) * PAGE_SIZE, pageY * PAGE_SIZE);

    if (rowBuckets.length === 0 && colDataPoint) {
      colDataPoint.getDataHeaders().forEach(dataHeader => {
        colDataPoint.addToTempHeaders(dataHeader);
      })
    }

    rowBuckets.forEach(rowBucket => {
      const rowHeader = getHeader(rowDataPoint, rowBucket["key"])
      rowDataPoint.addToTempHeaders(rowHeader);
      const colBuckets = rowBucket["col_agg_c"]["buckets"].slice((pageX - 1) * PAGE_SIZE, pageX * PAGE_SIZE)

      if (colBuckets.length === 0 && colDataPoint) {
        colDataPoint.getDataHeaders().forEach(dataHeader => {
          colDataPoint.addToTempHeaders(dataHeader);
        })
      }

      if (colDataPoint.customize_display) {
        const bucketNames = colBuckets.map(e => e.key)
        colDataPoint?.getDataHeaders()?.forEach(dataHeader => {
          if (!bucketNames.includes(dataHeader)) {
            colDataPoint.addToTempHeaders(dataHeader);
          }
        })
      }

      colBuckets.forEach(colBucket => {
        const colHeader = getHeader(colDataPoint, colBucket["key"])
        colDataPoint.addToTempHeaders(colHeader);
        valueDataPoint.addToValueMap(colBucket["val_agg_c"]["value"], rowHeader, colHeader);
      });
    });
  });
}

export const getHeader = (dataPoint, key) => {
  let header = key
  if (dataPoint.isAccountRelationship) {
    const regex = new RegExp(`(^${dataPoint.referenceNameOrDefault} - )`, 'gi')
    header = key.replace(regex, "")
  }
  return header
}

export const getLiveColumnHeaders = (visualization): any[] => {
  const headers = visualization.columnDataPointsArray.map(p => p.configuredTempHeaders).flatten();
  const rowPoint = visualization.rowDataPointsArray[0];
  headers.unshift({title: rowPoint.title, client_id: rowPoint.client_id});

  return headers;
}

export const getLiveRowHeaders = (visualization): any[] => {
  const headers = visualization.rowDataPointsArray.map(p => p.configuredTempHeaders).flatten();
  headers.shift();

  return headers;
}

export const getLiveTableHeaders = (visualization): any[] => {
  return visualization.columnDataPointsArray.map(p => {
    return {
      c_span: p.configuredTempHeaders.length,
      title: p.title
    }
  }).flatten();
}

export const getLiveTableBody = (visualization): any[] => {
  const tableRows = [];
  const colHeaders = getLiveColumnHeaders(visualization);
  colHeaders.shift();
  const rowHeaders = getLiveRowHeaders(visualization);

  rowHeaders.forEach(rowHeader => {
    const currRow = [rowHeader.title];

    colHeaders.forEach(colHeader => {
      const valueDataPoint = visualization.valueDataPointsArray.find(vp => vp.referenced_row_client_id === rowHeader.client_id && vp.referenced_col_client_id === colHeader.client_id);
      let value: any = "--";

      if (!valueDataPoint) {
        currRow.push(value);
        return;
      } else if (rowHeader.isHeader) {
        currRow.push("");
        return;
      } else if (colHeader.title === "Total" && rowHeader.title === "Total") {
        value = Object.values(valueDataPoint.colTotals).reduce((total: number, acc: number) => total + acc, 0);
      } else if (colHeader.title === "Total") {
        value = valueDataPoint.rowTotals[rowHeader.title] || 0;
      } else if (rowHeader.title === "Total") {
        value = valueDataPoint.colTotals[colHeader.title] || 0;
      } else {
        value = valueDataPoint.getValue(rowHeader.title, colHeader.title);
      }

      if (valueDataPoint.isCurrency && valueDataPoint.aggregation_type !== DataPointAggregationType.Count && value !== "--") {
        value = NumberHelper.numberToCurrency("en-US", "USD", value);
      }

      if (valueDataPoint.value_format_type === DataPointValueFormatType.Percentage && valueDataPoint.aggregation_type !== DataPointAggregationType.Count && value !== "--" && NumberHelper.isNumber(value)) {
        value = (value * 100).toString() + "%"
      }

      currRow.push(value);
    });

    tableRows.push(currRow);
  });

  return tableRows;
}

export const clearData = (visualization): void => {
  visualization.allDataPoints.forEach(point => {
    point.valueMap = {};
    point.tempHeaders = [];
    point.colTotals = {};
    point.rowTotals = {};
  })
}